import React, { useMemo, useState } from 'react';
import {
  computeDraftSendEligibility,
  sendDraftPurchaseOrder,
} from '../../services/purchaseOrderService';

const DRAFT_LIKE_STATUSES = new Set(['draft', 'blocked_no_email']);

function summarizeBlockerReasons(blockedList) {
  const reasons = Array.from(
    new Set(blockedList.map(({ eligibility }) => eligibility.blockerReason).filter(Boolean))
  );
  return reasons.join('; ');
}

function classifyDrafts(rows) {
  const sendable = [];
  const blocked = [];
  rows.forEach((row) => {
    if (!DRAFT_LIKE_STATUSES.has(row.delivery_status)) return;
    if (row.is_latest_revision === false) return;
    // Out of scope: bulk-sending revised/amendment POs.
    if ((row.revision_number || 0) > 0) return;
    const eligibility = computeDraftSendEligibility({
      vendorContactSnapshot: row.vendor_contact_snapshot,
      vendorNameSnapshot: row.vendor_name_snapshot,
      vendorId: row.vendor_id,
      lineCount: row.line_count,
      items: [],
    });
    if (eligibility.canSend) {
      sendable.push({ row, eligibility });
    } else {
      blocked.push({ row, eligibility });
    }
  });
  return { sendable, blocked };
}

export default function BulkSendDraftsButton({ rows, onChanged }) {
  const { sendable, blocked } = useMemo(() => classifyDrafts(rows), [rows]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null);

  const bulkInFlight = !!progress?.running;
  if (sendable.length < 2) return null;

  const uniqueVendorNames = Array.from(new Set(sendable.map(({ row }) => row.vendor_name_snapshot || 'Unknown vendor')));

  const startBulkSend = async () => {
    setConfirmOpen(false);
    const initial = sendable.map(({ row }) => ({
      poId: row.id,
      poNumber: row.po_number,
      vendorName: row.vendor_name_snapshot || 'Unknown vendor',
      status: 'pending',
      message: '',
    }));
    setProgress({ running: true, results: initial, doneCount: 0 });

    const results = initial.map((r) => ({ ...r }));
    let done = 0;
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < results.length; i += 1) {
      results[i] = { ...results[i], status: 'sending' };
      setProgress({ running: true, results: results.map((r) => ({ ...r })), doneCount: done });
      try {
        // eslint-disable-next-line no-await-in-loop
        await sendDraftPurchaseOrder(results[i].poId, {});
        results[i] = { ...results[i], status: 'success', message: 'Sent' };
        succeeded += 1;
      } catch (err) {
        results[i] = {
          ...results[i],
          status: 'error',
          message: err?.message || 'Failed to send',
        };
        failed += 1;
      }
      done += 1;
      setProgress({
        running: done < results.length,
        results: results.map((r) => ({ ...r })),
        doneCount: done,
        summary: done === results.length ? { succeeded, failed, total: results.length } : null,
      });
    }

    if (typeof onChanged === 'function') onChanged();
  };

  const closeProgress = () => {
    if (progress?.running) return;
    setProgress(null);
  };

  return (
    <>
      <div style={barStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#3D2518', fontSize: 14 }}>
            {sendable.length} drafts ready to send
          </div>
          <div style={{ color: '#5A4A42', fontSize: 12, marginTop: 2 }}>
            {uniqueVendorNames.slice(0, 3).join(', ')}
            {uniqueVendorNames.length > 3 ? `, +${uniqueVendorNames.length - 3} more` : ''}
            {blocked.length > 0 && (
              <span style={{ color: '#b71c1c', marginLeft: 8 }}>
                · {blocked.length} draft{blocked.length === 1 ? '' : 's'} excluded ({summarizeBlockerReasons(blocked)})
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          style={{ ...primaryBtnStyle, background: bulkInFlight ? '#9E9E9E' : primaryBtnStyle.background, borderColor: bulkInFlight ? '#9E9E9E' : primaryBtnStyle.borderColor, cursor: bulkInFlight ? 'not-allowed' : 'pointer' }}
          onClick={() => { if (!bulkInFlight) setConfirmOpen(true); }}
          disabled={bulkInFlight}
        >
          {bulkInFlight ? 'Sending…' : `📤 Send All Ready Drafts (${sendable.length})`}
        </button>
      </div>

      {confirmOpen && (
        <Overlay onClose={() => setConfirmOpen(false)}>
          <h3 style={modalTitleStyle}>Send {sendable.length} draft purchase orders?</h3>
          <div style={{ fontSize: 13, color: '#5A4A42', marginBottom: 10 }}>
            Each draft will be dispatched one after another. You'll see per-vendor results.
          </div>
          <div style={listBoxStyle}>
            {sendable.map(({ row, eligibility }) => (
              <div key={row.id} style={listRowStyle}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#3D2518' }}>{row.vendor_name_snapshot || 'Unknown vendor'}</div>
                  <div style={{ color: '#8B7B73', fontSize: 11 }}>
                    {row.po_number} · {row.line_count || 0} item{(row.line_count || 0) === 1 ? '' : 's'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', color: '#5A4A42', fontSize: 11, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(eligibility.recipients || []).slice(0, 2).join(', ')}
                  {(eligibility.recipients || []).length > 2 ? ` +${eligibility.recipients.length - 2}` : ''}
                </div>
              </div>
            ))}
          </div>
          {blocked.length > 0 && (
            <div style={blockedNoteStyle}>
              {blocked.length} draft{blocked.length === 1 ? ' is' : 's are'} excluded:{' '}
              {summarizeBlockerReasons(blocked) || 'not ready to send'}.
            </div>
          )}
          <div style={modalBtnRowStyle}>
            <button type="button" style={cancelBtnStyle} onClick={() => setConfirmOpen(false)}>Cancel</button>
            <button type="button" style={confirmBtnStyle} onClick={startBulkSend}>
              Send {sendable.length}
            </button>
          </div>
        </Overlay>
      )}

      {progress && (
        <Overlay onClose={closeProgress}>
          <h3 style={modalTitleStyle}>
            {progress.running ? 'Sending drafts…' : 'Bulk send complete'}
          </h3>
          <div style={{ fontSize: 13, color: '#5A4A42', marginBottom: 8 }}>
            {progress.running
              ? `${progress.doneCount} of ${progress.results.length} processed…`
              : progress.summary
              ? `${progress.summary.succeeded} sent, ${progress.summary.failed} failed.`
              : 'Done.'}
          </div>
          <div style={listBoxStyle}>
            {progress.results.map((r) => (
              <div key={r.poId} style={listRowStyle}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#3D2518' }}>{r.vendorName}</div>
                  <div style={{ color: '#8B7B73', fontSize: 11 }}>{r.poNumber}</div>
                </div>
                <div style={statusPillStyle(r.status)}>
                  {r.status === 'pending' && 'Waiting'}
                  {r.status === 'sending' && 'Sending…'}
                  {r.status === 'success' && '✓ Sent'}
                  {r.status === 'error' && `✗ ${r.message || 'Failed'}`}
                </div>
              </div>
            ))}
          </div>
          <div style={modalBtnRowStyle}>
            <button
              type="button"
              style={{ ...confirmBtnStyle, background: progress.running ? '#9E9E9E' : '#526B7C', borderColor: progress.running ? '#9E9E9E' : '#3F5563', cursor: progress.running ? 'not-allowed' : 'pointer' }}
              onClick={closeProgress}
              disabled={progress.running}
            >
              {progress.running ? 'Please wait…' : 'Close'}
            </button>
          </div>
        </Overlay>
      )}
    </>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div
      style={overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

const barStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: '#fff',
  border: '1.5px solid #C8E6C9',
  borderLeft: '4px solid #2E7D32',
  borderRadius: 12,
  padding: '10px 14px',
  marginBottom: 12,
  flexWrap: 'wrap',
};

const primaryBtnStyle = {
  background: '#2E7D32',
  color: '#fff',
  border: '1px solid #1b5e20',
  padding: '8px 16px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10, 15, 25, 0.6)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  fontFamily: "'Open Sans', sans-serif",
};

const modalCardStyle = {
  background: '#fff',
  border: '2px solid rgb(212, 196, 176)',
  borderRadius: 10,
  padding: 22,
  maxWidth: 560,
  width: '100%',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
};

const modalTitleStyle = {
  margin: '0 0 12px 0',
  color: '#3D2518',
  fontSize: 18,
  fontWeight: 700,
  fontFamily: 'Montserrat, sans-serif',
};

const listBoxStyle = {
  background: '#FAF6F2',
  border: '1px solid #E9DFD6',
  borderRadius: 8,
  padding: 10,
  maxHeight: 280,
  overflowY: 'auto',
};

const listRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  padding: '6px 0',
  borderBottom: '1px dashed #EADFD2',
  fontSize: 13,
};

const blockedNoteStyle = {
  marginTop: 10,
  padding: '8px 10px',
  background: '#FFF3E0',
  border: '1px solid #FFE0B2',
  borderRadius: 8,
  fontSize: 12,
  color: '#7a4f00',
};

const modalBtnRowStyle = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 14,
};

const cancelBtnStyle = {
  background: '#fff',
  border: '1px solid rgb(212, 196, 176)',
  color: '#5A4A42',
  padding: '8px 16px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const confirmBtnStyle = {
  background: '#2E7D32',
  color: '#fff',
  border: '1px solid #1b5e20',
  padding: '8px 18px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const statusPillStyle = (status) => {
  const palette = {
    pending: { bg: '#F0EDED', fg: '#9A8A84' },
    sending: { bg: '#FFF3E0', fg: '#E65100' },
    success: { bg: '#E8F5E9', fg: '#2E7D32' },
    error: { bg: '#FDECEA', fg: '#b71c1c' },
  }[status] || { bg: '#F0EDED', fg: '#9A8A84' };
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 999,
    background: palette.bg,
    color: palette.fg,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    maxWidth: 240,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
};
